import React, { useCallback, useEffect, useState } from 'react';
import { PinCodeViewProps } from './models';
import { useTranslation } from '@sunbit-dev/translations';
import { VerificationCode } from '@sunbit-dev/purchase-common/src/components/verificationCode/VerificationCode';
import { loanApplicationNetworkService } from '~/services/LoanApplicationNetworkService';
import { EventsNames } from '~/enums/Events';
import { addError } from '~/store/reducers/errors/errors';
import { useDispatch, useSelector } from 'react-redux';
import { ErrorCodes } from '~/enums/Errors';
import { Slogan } from '../slogan/Slogan';
import styled from 'styled-components';
import * as styles from './styles';
import { useTrack } from '~/hooks/track';
import { stringToPhoneString } from '@sunbit-dev/purchase-common/src/utilities/string';
import { PinCodeFooter } from './components/pinCodeFooter/PinCodeFooter';
import { getErrorModalDataTestId } from '@sunbit-dev/purchase-common/src/utilities/errors';
import { ErrorModal } from '~/components/app/components/errorModal/ErrorModal';
import { ErrorTexts } from '~/store/reducers/errors/models';
import { PrivacyAllowanceLayer } from '@sunbit-dev/purchase-common/src/components/privacyAllowanceLayer/PrivacyAllowanceLayer';
import { PagesTestIds } from '~/enums/TestIds';
import { TrackCategory } from '@sunbit-dev/purchase-common/src/services/networkService/models';
import messageService from '~/services/MessageService';
import PlaceInFlowPages from '~/services/MessageService/PlaceInFlowPages';
import { LoanApplicationOrigin } from '~/enums/LoanApplicationOrigin';
import * as purchaseStore from '~/store/reducers/purchase/purchase';

export const PinCodeView = styled(
  ({
    userMaskedPhoneNumber,
    onSuccess,
    className,
    onResendClick,
    onRefreshFailure,
    pinCodeMethod,
  }: PinCodeViewProps) => {
    const { t } = useTranslation('landing_page', { keyPrefix: 'pin_code_view' });

    const [showExceededVerificationPopup, setShowExceededVerificationPopup] = useState<boolean>(false);
    const popupText: ErrorTexts = t('exceeded_verification_attempts');

    const [verificationError, setVerificationError] = useState<string | undefined>();

    const { purchaseInfo } = useSelector(purchaseStore.getStore);

    const onPopupClose = useCallback(() => {
      setShowExceededVerificationPopup(false);
      onResendClick();
    }, [setShowExceededVerificationPopup]);

    const dispatch = useDispatch();
    const { track } = useTrack();

    useEffect(() => {
      track({ eventName: EventsNames.pinCodeView, type: TrackCategory.View });
    }, []);

    useEffect(() => {
      if (purchaseInfo?.loanApplicationOrigin === LoanApplicationOrigin.ONLINE_SDK) {
        messageService.messagePlaceInFlow(PlaceInFlowPages.PHONE_VERIFICATION, null);
      }
    }, [purchaseInfo]);

    const onFillPinCode = useCallback(
      async ({ verificationCode }) => {
        setVerificationError(undefined);
        const eventName = EventsNames.validatePinCode;
        try {
          await loanApplicationNetworkService.login({
            verificationCode,
            pinCodeMethod,
            eventName,
            onRefreshFailure,
          });
          await onSuccess();
        } catch (error: any) {
          switch (error?.code) {
            case ErrorCodes.WrongCode:
            case ErrorCodes.ExpiredCode:
            case ErrorCodes.VerificationCodeNeeded:
              setVerificationError(t('pin_code_incorrect'));
              break;
            case ErrorCodes.ExceededVerificationAttempts:
              setShowExceededVerificationPopup(true);
              break;
            default:
              dispatch(addError({ eventName, payload: error }));
          }
        }
      },
      [setVerificationError, onSuccess, onRefreshFailure]
    );

    const onVerificationCodeChange = useCallback(() => {
      setVerificationError(undefined);
    }, [setVerificationError]);

    return (
      <PrivacyAllowanceLayer>
        <div className={className} data-testid={PagesTestIds.PinCodeView}>
          <Slogan className="slogan" />
          <p className="pin-code-sent">
            {t('pin_code_sent', { userPhoneNumber: stringToPhoneString(userMaskedPhoneNumber) })}
          </p>
          <div className="verification-code-wrapper">
            <VerificationCode
              error={verificationError}
              onFill={onFillPinCode}
              length={6}
              onChange={onVerificationCodeChange}
            />
            <p className="timing">{t('pin_code_timing')}</p>
          </div>
          <PinCodeFooter className="pin-code-footer" onResendClick={onResendClick} />
        </div>
        {showExceededVerificationPopup && (
          <ErrorModal
            onClose={onPopupClose}
            texts={popupText}
            dataTestId={getErrorModalDataTestId(ErrorCodes.ExceededVerificationAttempts)}
          />
        )}
      </PrivacyAllowanceLayer>
    );
  }
)`
  ${styles.PinCodeView}
`;
